import React from 'react';
import styled from 'styled-components';
import { FaLinkedin, FaEnvelope, FaCheck, FaUsers, FaAward, FaShieldAlt, FaLightbulb, FaRocket } from 'react-icons/fa';
import Layout from '../components/Layout';
import SEO from '../components/SEO';
import Hero from '../components/Hero';
import Section from '../components/Section';
import Button from '../components/Button';

const TeamSection = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 3rem;
  margin: 3rem 0;
  
  @media (min-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const TeamMember = styled.div`
  background-color: white;
  border-radius: 1rem;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.05);
  overflow: hidden;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  
  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 15px 35px rgba(0, 0, 0, 0.1);
  }
`;

const TeamMemberContent = styled.div`
  padding: 2rem;
`;

const TeamMemberPhoto = styled.div`
  height: 300px;
  position: relative;
  overflow: hidden;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.5s ease;
  }
  
  ${TeamMember}:hover img {
    transform: scale(1.05);
  }
`;

const TeamMemberName = styled.h3`
  font-size: 1.5rem;
  margin-bottom: 0.5rem;
`;

const TeamMemberTitle = styled.h4`
  color: var(--blue);
  font-weight: 500;
  margin-bottom: 1rem;
  font-size: 1.1rem;
`;

const TeamMemberBio = styled.p`
  color: var(--gray);
  margin-bottom: 1.5rem;
  line-height: 1.6;
`;

const SocialLinks = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 1rem;
`;

const SocialLink = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background-color: #f5f5f5;
  border-radius: 50%;
  color: var(--blue);
  transition: background-color 0.3s ease, color 0.3s ease;
  
  &:hover {
    background-color: var(--blue);
    color: white;
  }
`;

const ValuesGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.5rem;
  margin: 3rem 0;
  
  @media (min-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const ValueItem = styled.div`
  display: flex;
  gap: 1.5rem;
  padding: 1.5rem;
  background-color: white;
  border-radius: 0.75rem;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.05);
  transition: transform 0.3s ease;
  
  &:hover {
    transform: translateY(-5px);
  }
`;

const ValueIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 60px;
  height: 60px;
  background: linear-gradient(135deg, #007bff 0%, #0056b3 100%);
  border-radius: 50%;
  color: white;
  font-size: 1.5rem;
  flex-shrink: 0;
`;

const ValueContent = styled.div`
  flex: 1;
`;

const ValueTitle = styled.h3`
  font-size: 1.3rem;
  margin-bottom: 0.75rem;
`;

const ValueDescription = styled.p`
  color: var(--gray);
  font-size: 0.95rem;
  line-height: 1.6;
`;

const StatsSection = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 2rem;
  margin: 3rem 0;
  
  @media (min-width: 768px) {
    grid-template-columns: repeat(4, 1fr);
  }
`;

const StatItem = styled.div`
  text-align: center;
  padding: 2rem 1rem;
  background-color: white;
  border-radius: 0.75rem;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.05);
`;

const StatNumber = styled.div`
  font-size: 2.5rem;
  font-weight: 700;
  color: var(--blue);
  margin-bottom: 0.5rem;
`;

const StatLabel = styled.div`
  color: var(--gray);
  font-size: 0.9rem;
`;

const CTASection = styled.div`
  background: linear-gradient(135deg, #007bff 0%, #0056b3 100%);
  padding: 4rem 2rem;
  border-radius: var(--border-radius-lg);
  text-align: center;
  color: var(--white);
  margin: 3rem 0;
`;

const CTATitle = styled.h2`
  font-size: 2.5rem;
  margin-bottom: 1.5rem;
`;

const CTADescription = styled.p`
  font-size: 1.25rem;
  margin-bottom: 2rem;
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;
`;

const SobreNosotrosPage = () => {
  return (
    <Layout>
      <SEO 
        title="Sobre Nosotros | Web a Bajo Coste"
        description="Conoce nuestro equipo en Web a Bajo Coste. Somos especialistas en crear páginas web baratas y de calidad para pymes y autónomos."
        keywords="equipo web a bajo coste, quienes somos, agencia web barata"
        canonical="https://webabajocoste.es/sobre-nosotros"
      />
      
      <Hero 
        title="Sobre Nosotros"
        subtitle="Conoce al equipo detrás de Web a Bajo Coste"
        backgroundImage="/fotos generales para el proyecto/sobre-nosotros-hero.webp"
      />
      
      <Section 
        title="Quiénes Somos"
        subtitle="Un pequeño equipo con grandes resultados"
      >
        <p>
          Web a Bajo Coste nació en 2021 con un objetivo claro: democratizar el acceso a páginas web 
          profesionales para pequeños negocios y autónomos. Entendemos que muchas pymes no pueden permitirse 
          gastar miles de euros en una web, pero tampoco deben conformarse con soluciones genéricas o de baja calidad.
        </p>
        <p>
          Nuestro enfoque se basa en procesos optimizados, plantillas personalizables de alta calidad y una 
          metodología de trabajo que nos permite ofrecer webs profesionales a precios accesibles, sin renunciar a un 
          diseño atractivo, un buen rendimiento y una excelente optimización para buscadores.
        </p>
        
        <TeamSection>
          <TeamMember>
            <TeamMemberPhoto>
              <img src="/sobre nosotros/foto-miguel.webp" alt="Miguel García" />
            </TeamMemberPhoto>
            <TeamMemberContent>
              <TeamMemberName>Miguel Vela</TeamMemberName>
              <TeamMemberTitle>Co-fundador y Director de Desarrollo</TeamMemberTitle>
              <TeamMemberBio>
                Con más de 10 años de experiencia en desarrollo web y UI/UX, Miguel lidera el desarrollo técnico de 
                nuestros proyectos. Es experto en React, TypeScript y optimización de rendimiento web. Apasionado 
                por crear experiencias web que no solo sean bonitas, sino también rápidas y funcionales.
              </TeamMemberBio>
              <SocialLinks>
                <SocialLink href="https://www.linkedin.com/in/miguel-vela-batanero/" target="_blank" rel="noopener noreferrer">
                  <FaLinkedin />
                </SocialLink>
                <SocialLink href="mailto:miguel@webabajocoste.es">
                  <FaEnvelope />
                </SocialLink>
              </SocialLinks>
            </TeamMemberContent>
          </TeamMember>
          
          <TeamMember>
            <TeamMemberPhoto>
              <img src="/sobre nosotros/foto-jaime.webp" alt="Jaime Sánchez" />
            </TeamMemberPhoto>
            <TeamMemberContent>
              <TeamMemberName>Jaime Vela</TeamMemberName>
              <TeamMemberTitle>Co-fundador y Director de Marketing</TeamMemberTitle>
              <TeamMemberBio>
                Especialista en SEO y marketing digital con más de 8 años de experiencia. Jaime se encarga de 
                que cada web no solo se vea bien, sino que también convierta visitantes en clientes y 
                posicione bien en buscadores. Ha trabajado con más de 200 pymes ayudándolas a mejorar su 
                presencia online.
              </TeamMemberBio>
              <SocialLinks>
                <SocialLink href="https://www.linkedin.com/in/jaime-vela-batanero-b084b4123/" target="_blank" rel="noopener noreferrer">
                  <FaLinkedin />
                </SocialLink>
                <SocialLink href="mailto:jaime@webabajocoste.es">
                  <FaEnvelope />
                </SocialLink>
              </SocialLinks>
            </TeamMemberContent>
          </TeamMember>
        </TeamSection>
      </Section>
      
      <Section 
        title="Nuestros Valores"
        subtitle="Los principios que guían nuestro trabajo"
        background="light"
      >
        <ValuesGrid>
          <ValueItem>
            <ValueIcon><FaCheck /></ValueIcon>
            <ValueContent>
              <ValueTitle>Calidad accesible</ValueTitle>
              <ValueDescription>
                Creemos que un presupuesto limitado no debe significar una web de baja calidad. Optimizamos 
                procesos para ofrecer soluciones profesionales a precios accesibles.
              </ValueDescription>
            </ValueContent>
          </ValueItem>
          
          <ValueItem>
            <ValueIcon><FaUsers /></ValueIcon>
            <ValueContent>
              <ValueTitle>Enfoque en el cliente</ValueTitle>
              <ValueDescription>
                Nos adaptamos a las necesidades de cada cliente, escuchando activamente y ofreciendo soluciones 
                personalizadas que realmente resuelvan sus problemas de negocio.
              </ValueDescription>
            </ValueContent>
          </ValueItem>
          
          <ValueItem>
            <ValueIcon><FaAward /></ValueIcon>
            <ValueContent>
              <ValueTitle>Excelencia técnica</ValueTitle>
              <ValueDescription>
                Aunque nuestros precios son económicos, no escatimamos en calidad técnica. Cada web está 
                optimizada para velocidad, SEO y experiencia de usuario.
              </ValueDescription>
            </ValueContent>
          </ValueItem>
          
          <ValueItem>
            <ValueIcon><FaShieldAlt /></ValueIcon>
            <ValueContent>
              <ValueTitle>Transparencia</ValueTitle>
              <ValueDescription>
                Sin sorpresas ni costes ocultos. Somos claros con lo que incluye cada paquete y mantenemos 
                una comunicación honesta durante todo el proyecto.
              </ValueDescription>
            </ValueContent>
          </ValueItem>
          
          <ValueItem>
            <ValueIcon><FaLightbulb /></ValueIcon>
            <ValueContent>
              <ValueTitle>Innovación práctica</ValueTitle>
              <ValueDescription>
                Implementamos soluciones innovadoras pero prácticas, centrándonos en lo que realmente aporta 
                valor al cliente y evitando complejidades innecesarias.
              </ValueDescription>
            </ValueContent>
          </ValueItem>
          
          <ValueItem>
            <ValueIcon><FaRocket /></ValueIcon>
            <ValueContent>
              <ValueTitle>Rapidez y eficiencia</ValueTitle>
              <ValueDescription>
                Valoramos el tiempo de nuestros clientes. Hemos optimizado nuestros procesos para entregar 
                proyectos en tiempos récord sin comprometer la calidad.
              </ValueDescription>
            </ValueContent>
          </ValueItem>
        </ValuesGrid>
      </Section>
      
      <Section 
        title="Web a Bajo Coste en cifras"
        subtitle="Nuestro impacto desde 2021"
      >
        <StatsSection>
          <StatItem>
            <StatNumber>200+</StatNumber>
            <StatLabel>Webs entregadas</StatLabel>
          </StatItem>
          
          <StatItem>
            <StatNumber>98%</StatNumber>
            <StatLabel>Clientes satisfechos</StatLabel>
          </StatItem>
          
          <StatItem>
            <StatNumber>8</StatNumber>
            <StatLabel>Días de entrega media</StatLabel>
          </StatItem>
          
          <StatItem>
            <StatNumber>25+</StatNumber>
            <StatLabel>Sectores atendidos</StatLabel>
          </StatItem>
        </StatsSection>
        
        <p>
          Nos enorgullece haber ayudado a más de 200 pequeños negocios a mejorar su presencia online con 
          webs profesionales, rápidas y optimizadas para SEO a precios accesibles. Nuestros clientes han 
          conseguido aumentar sus ventas, mejorar su imagen de marca y captar nuevos clientes gracias a 
          sus nuevas páginas web.
        </p>
      </Section>
      
      <Section>
        <CTASection>
          <CTATitle>¿Listo para trabajar con nosotros?</CTATitle>
          <CTADescription>
            Si buscas una web profesional a un precio accesible, somos tu mejor opción. Contacta con 
            nosotros hoy mismo y te asesoraremos sin compromiso.
          </CTADescription>
          <Button 
            as="a" 
            href="/contacto" 
            size="large"
            variant="secondary"
          >
            Contactar ahora
          </Button>
        </CTASection>
      </Section>
    </Layout>
  );
};

export default SobreNosotrosPage; 